import { useI18n } from "@baton/i18n";
import { ArrowLeft, Bell, House, Menu, MoreHorizontal, Search } from "@baton/icons";
import { buttonVariants, cn, DropdownMenu, IconButton, SkeletonBlock, Tooltip, type MenuItem } from "@baton/ui";
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router";
import { returnTo, type Origin } from "../history/origin.ts";
import { globalShortcutKeys } from "../keyboard/keyboard.ts";

/**
 * FR-015-007, `contracts/screens.md` § Top bar, `contracts/design-system.md` § Navigation
 * and structure: the route's breadcrumbs truncating to an overflow menu at narrow widths,
 * the parent path alone, every crumb a way back, since the page's own title is its heading and the
 * bar never repeats it (2026-10-03, by the owner's approval of the product review, which found
 * "Team / Inbox" over "Inbox" and the task page naming its project and identifier twice),
 * the screen's primary actions, the palette trigger, the notification bell duplicating the
 * inbox badge on narrow viewports, and the drawer toggle below `--bp-lg`. A screen publishes
 * its crumbs and actions through `useTopBar`. New item moved out of the bar and into each
 * section that creates one (2026-09-25, by the owner's decision, reversing 2026-09-16: one
 * button for every kind of creation read as generic and hid the create action a step below
 * where a section's own list already offered it). The plus came back on 2026-09-27, by the owner's
 * decision, as the quick-create menu rather than New item (`quickCreate.tsx`, § 020 Accounts and
 * pipeline, the block of 2026-09-27), before the palette trigger. The palette trigger is a quiet icon button
 * at the bar's end, named in its tooltip, the same size as the bell and the drawer toggle
 * (2026-09-17, by the owner's decision); `Cmd K` stays on the trigger's `aria-keyshortcuts`
 * and in the shortcut reference.
 *
 * 2026-10-07, by the owner's request from the review with Mejd of 2026-10-06, where going back had no
 * control on some pages and landed on the wrong page on others: a page under one of its mode's destinations (`isNestedPage`) opens its bar with Back, a quiet
 * button of an arrow and the word, named for where it leads ("Back to Halden Savings Bank"). It returns
 * to the page the person came from, walking the history back to it (`shell/history/origin.ts`), so the
 * browser's Back and Forward agree with it; a page with no such origin, opened by a direct link, goes
 * to its logical parent, the bar's last crumb or the parent the screen names. The crumbs follow it,
 * the hierarchy beside the way back. Below `--bp-md` it keeps the arrow alone, named the same.
 */
export interface Crumb {
  readonly label: string;
  readonly to?: string;
  /** The first crumb's glyph; the workspace's house when absent. */
  readonly icon?: ReactNode;
  /** The label is not known yet (the Team's crumb while the Team loads), so a text placeholder stands in its place rather than an identifier. */
  readonly loading?: boolean;
}

export interface TopBarSlots {
  readonly crumbs: readonly Crumb[];
  readonly actions: ReactNode;
  /** The page's logical parent where it is not the last crumb (a project's tasks, whose crumb is the project itself), which Back leads to when the page has no origin. */
  readonly parent?: Crumb;
}

interface TopBarStore extends TopBarSlots {
  readonly publish: (slots: TopBarSlots) => void;
}

const TopBarContext = createContext<TopBarStore | null>(null);

export function useTopBarStore(): TopBarStore {
  const [slots, setSlots] = useState<TopBarSlots>({ crumbs: [], actions: null });
  return useMemo(() => ({ ...slots, publish: setSlots }), [slots]);
}

export const TopBarProvider = TopBarContext.Provider;

/** A screen declares its crumbs and actions; the top bar renders them. */
export function useTopBar(): TopBarStore {
  const value = useContext(TopBarContext);
  if (value === null) throw new Error("useTopBar must be used inside the shell");
  return value;
}

export interface BreadcrumbsProps {
  readonly segments: readonly Crumb[];
  /** The parent path, the page's own title left to its heading; crumbs beyond this many collapse into an overflow menu, the first and last staying visible. */
  readonly max?: number;
}

export function Breadcrumbs({ segments, max = 3 }: BreadcrumbsProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const overflow = segments.length > max ? segments.slice(1, segments.length - 1) : [];
  const visible = overflow.length > 0 ? [segments[0], segments[segments.length - 1]].filter((s): s is Crumb => s !== undefined) : segments;
  return (
    <nav className="b-topbar__crumbs" aria-label={t("nav.breadcrumbs")}>
      <ol>
        {visible.map((segment, index) => (
          <li key={`${segment.label}-${index}`} className="b-topbar__crumb">
            {index > 0 && (
              <span className="b-topbar__separator" aria-hidden="true">
                /
              </span>
            )}
            {index === 1 && overflow.length > 0 && (
              <>
                <DropdownMenu
                  label={t("nav.moreCrumbs")}
                  items={overflow.map<MenuItem>((crumb, i) => ({
                    id: `crumb-${i}`,
                    label: crumb.label,
                    onSelect: () => {
                      if (crumb.to !== undefined) void navigate(crumb.to);
                    },
                  }))}
                  trigger={<IconButton size="sm" icon={<MoreHorizontal />} label={t("nav.moreCrumbs")} />}
                />
                <span className="b-topbar__separator" aria-hidden="true">
                  /
                </span>
              </>
            )}
            {index === 0 && (
              <span className="b-topbar__crumb-icon" aria-hidden="true">
                {segment.icon ?? <House />}
              </span>
            )}
            {segment.loading === true ? <SkeletonBlock variant="text" className="b-topbar__crumb-placeholder" /> : segment.to !== undefined ? <Link to={segment.to}>{segment.label}</Link> : <span>{segment.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** Where Back leads: the origin the person came from, or the logical parent when there is none. */
export interface BackTarget {
  readonly to: string;
  /** The destination's name, or null while it has none (an origin whose screen named itself before loading). */
  readonly label: string | null;
  readonly origin: Origin | null;
}

/** The parent a page's Back falls back to: the screen's own `parent`, else the last crumb that links elsewhere. */
export function parentOf(slots: Pick<TopBarSlots, "crumbs" | "parent">, pathname: string): Crumb | null {
  if (slots.parent !== undefined) return slots.parent;
  return [...slots.crumbs].reverse().find((crumb) => crumb.to !== undefined && crumb.to !== pathname) ?? null;
}

export function BackControl({ target }: { readonly target: BackTarget }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const name = target.label === null || target.label.length === 0 ? t("nav.back") : t("nav.backTo", { place: target.label });
  return (
    <Tooltip content={name}>
      <Link
        to={target.to}
        className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "b-topbar__back")}
        data-slot="button"
        data-variant="ghost"
        data-size="sm"
        aria-label={name}
        onClick={(event) => {
          // A press with a modifier opens the destination's address as any link does; a plain press walks back to the origin, or goes to the parent.
          if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          if (target.origin !== null) returnTo(target.origin);
          else void navigate(target.to);
        }}
      >
        <ArrowLeft aria-hidden="true" />
        <span className="b-topbar__back-label">{t("nav.back")}</span>
      </Link>
    </Tooltip>
  );
}

export interface TopBarProps {
  readonly ws: string;
  /** Back, on a page under one of its mode's destinations; null elsewhere. */
  readonly back: BackTarget | null;
  readonly crumbs: readonly Crumb[];
  readonly actions: ReactNode;
  readonly unread: number;
  /** The quick-create plus (`quickCreate.tsx`). */
  readonly create: ReactNode;
  readonly onOpenPalette: () => void;
  readonly onToggleDrawer: () => void;
}

export function TopBar({ ws, back, crumbs, actions, unread, create, onOpenPalette, onToggleDrawer }: TopBarProps) {
  const { t, format } = useI18n();
  return (
    <header className="b-topbar" role="banner">
      <span className="b-topbar__menu-toggle">
        <IconButton icon={<Menu />} label={t("shell.sidebar.toggle")} shortcut={globalShortcutKeys("sidebar")} onClick={onToggleDrawer} />
      </span>

      {back !== null && <BackControl target={back} />}
      <Breadcrumbs segments={crumbs} />
      <div className="b-topbar__actions">
        {actions}
        <span className="b-topbar__bell">
          <Tooltip content={t("nav.inbox")} shortcut={globalShortcutKeys("go-inbox")}>
            <NavLink to={`/${ws}/inbox`} className={`${buttonVariants({ variant: "ghost", size: "sm", iconOnly: true })} b-topbar__bell-link`} data-slot="button" data-variant="ghost" data-size="sm" data-icon-only="true" aria-label={unread > 0 ? `${t("nav.inbox")}, ${t("nav.unread", { count: unread })}` : t("nav.inbox")}>
              <Bell aria-hidden="true" />
              {unread > 0 && (
                <span className="b-rail__badge" aria-hidden="true">
                  {unread > 99 ? format.number(99) + "+" : format.number(unread)}
                </span>
              )}
            </NavLink>
          </Tooltip>
        </span>
        {create}
        <IconButton size="sm" icon={<Search />} label={t("palette.open")} shortcut={globalShortcutKeys("palette")} onClick={onOpenPalette} />
      </div>
    </header>
  );
}
